import { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import getAssetClass from './get-asset-class';
import getFundType from './get-fund-type';
import getTemplate, { TEMPLATES } from './get-profile-template';
import globals from '../../utils/globals';
import useSideRail from './use-side-rail';
import { SideRailContext } from '../../contexts';
import { useApiClient, useProfileAuxiliary } from '../../hooks/api';
import {
  accessTiaaElements,
  calculateOffsets
} from '../../utils/pageOffsetHelpers/pageOffsetHelpers';

const initialState = {};
const ProfileAppContext = createContext(initialState);
const { Provider: BaseProvider } = ProfileAppContext;
const { Provider: SideRailProvider } = SideRailContext;

export const Provider = ({
  channel,
  children,
  mpTicker = null,
  templateOverride,
  ticker,
  urlDomain,
  urlPrefix,
  xid,
  yyy
}) => {
  const apiClient = useApiClient({
    baseUrl: globals.apiUrl,
    baseAuthUrl: globals.authApiUrl,
    yyy
  });
  //Auxilliary call will fail if no xid/symbol is passed, adding this here so when we use mpTicker instead of xid/symbol it doesn't error out
  if (mpTicker) xid = 1;

  // console.log('app context, ticker - xid - mpticker |', ticker, xid, mpTicker);

  const tickerAsInt = parseInt(ticker, 10);
  const isTickerXid = !isNaN(tickerAsInt);
  const auxiliary = useProfileAuxiliary({
    client: apiClient,
    mpTicker,
    symbol: isTickerXid ? undefined : ticker,
    xid: isTickerXid ? ticker : xid
  });

  if (templateOverride) {
    templateOverride = decodeURIComponent(templateOverride);
    templateOverride = Object.values(TEMPLATES).find(t => t === templateOverride);
  }

  let assetClass;
  let fundType;
  let template = templateOverride || TEMPLATES.loading;
  if (auxiliary.isLoaded) {
    assetClass = getAssetClass(auxiliary.data.assetClass);
    fundType = getFundType(auxiliary.data.fundType);
    template =
      template === TEMPLATES.loading
        ? getTemplate(auxiliary.data.profileTemplate, auxiliary.data.isCit)
        : template;
  }

  const xref = {
    isLoaded: auxiliary.isLoaded,
    isLoading: auxiliary.isLoading,
    error: auxiliary.error,
    data: auxiliary.isLoaded ? auxiliary.data.xref : {}
  };

  const sideRail = useSideRail();

  //If page offsets are required based on bottom of the page, please add a new state variable.
  const [pageOffsetsTop, setpageOffsetsTop] = useState(0);

  // Handles setting Page Offsets when hosted via a TIAA integrated environment.
  // Basically, TIAA's own header sizes change depending on the site (MyTIAA, Microsites etc.).
  // Microsites have a header that collapses on scroll, hence the listeners.
  useEffect(() => {
    const MAX_ITERATIONS = 50;
    let tiaaElements = {};
    let updateAttempts = 0;

    (function initOffsets() {
      setTimeout(() => {
        tiaaElements = accessTiaaElements();

        if (tiaaElements?.mobileNav?.offsetHeight > 0 ||
            tiaaElements?.tiaaFooter?.offsetHeight > 0 ||
            tiaaElements?.tiaaHeader?.offsetHeight > 0 ||
            tiaaElements?.triageNav?.offsetHeight > 0) {
          if (pageOffsetsTop === 0) {
            setpageOffsetsTop(calculateOffsets(tiaaElements));
          }
        } else if (updateAttempts <= MAX_ITERATIONS) {
          updateAttempts++;
          initOffsets();
        }

        /*Timeout is to allow TIAA's site to load before grabbing the TIAA header height.*/
      }, 200);
    })();

    const handleScroll = _debounce(() => {
      setpageOffsetsTop(calculateOffsets(tiaaElements));
    }, 10);
    const handleResize = _debounce(() => {
      setpageOffsetsTop(calculateOffsets(tiaaElements));
    }, 10);

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return function cleanupListener() {
      window.removeEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleResize);
    };
  }, [pageOffsetsTop]);

  const state = {
    apiClient,
    assetClass,
    auxiliary,
    channel,
    fundType,
    pageOffsetsTop,
    sideRail,
    template,
    urlDomain,
    urlPrefix,
    xref,
    yyy
  };

  return (
    <BaseProvider value={state}>
      <SideRailProvider value={sideRail}>{children}</SideRailProvider>
    </BaseProvider>
  );
};

Provider.propTypes = {
  channel: PropTypes.string,
  children: PropTypes.node,
  mpTicker: PropTypes.string,
  templateOverride: PropTypes.string,
  ticker: PropTypes.string,
  urlDomain: PropTypes.string,
  urlPrefix: PropTypes.string,
  xid: PropTypes.string,
  yyy: PropTypes.string
};

export default ProfileAppContext;
